import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { Navbar } from "@/components/Navbar";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { LocaleLayout } from "@/components/LocaleLayout";

import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import FormExplainer from "@/pages/FormExplainer";
import WizardPage from "@/pages/WizardPage";
import RoadmapPage from "@/pages/RoadmapPage";
import ChecklistPage from "@/pages/ChecklistPage";
import DeadlineTracker from "@/pages/DeadlineTracker";
import PricingPage from "@/pages/PricingPage";
import ProfilePage from "@/pages/ProfilePage";
import DocumentsPage from "@/pages/DocumentsPage";
import TimelinePage from "@/pages/TimelinePage";
import FormGuidePage from "@/pages/FormGuidePage";
import DMVGuidePage from "@/pages/DMVGuidePage";
import GreenCardPage from "@/pages/GreenCardPage";
import GreenCardTimelinePage from "@/pages/GreenCardTimelinePage";
import GreenCardLotteryPage from "@/pages/GreenCardLotteryPage";
import DVFormGuidePage from "@/pages/DVFormGuidePage";
import DVLotteryDatesPage from "@/pages/DVLotteryDatesPage";
import DVPhotoRequirementsPage from "@/pages/DVPhotoRequirementsPage";
import DVAfterWinningPage from "@/pages/DVAfterWinningPage";
import DVCommonMistakesPage from "@/pages/DVCommonMistakesPage";
import DVCheckResultsPage from "@/pages/DVCheckResultsPage";
import DVEligibilityPage from "@/pages/DVEligibilityPage";
import ActivelyReviewedPage from "@/pages/ActivelyReviewedPage";
import CaseApprovedPage from "@/pages/CaseApprovedPage";
import BillingSuccessPage from "@/pages/BillingSuccessPage";
import BillingCancelPage from "@/pages/BillingCancelPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import ContactPage from "@/pages/ContactPage";
import GCLayout from "@/pages/gc/GCLayout";
import GCLanding from "@/pages/gc/GCLanding";
import GCGuidePage from "@/pages/gc/GCGuidePage";
import GCTimelinePage from "@/pages/gc/GCTimelinePage";
import GCDocumentsPage from "@/pages/gc/GCDocumentsPage";
import GCFAQPage from "@/pages/gc/GCFAQPage";
import { isGCDomain } from "@/pages/gc/gcDomain";

const LANGS = ["es", "pt", "ru", "uk", "hi"];

function publicRoutes() {
  return [
    <Route key="home" index element={<LandingPage />} />,
    <Route key="auth" path="auth" element={<AuthPage />} />,
    <Route key="pricing" path="pricing" element={<PricingPage />} />,
    <Route key="guides" path="guides" element={<FormGuidePage />} />,
    <Route key="guides-id" path="guides/:formId" element={<FormGuidePage />} />,
    <Route key="gc" path="green-card" element={<Navigate to="/gc" replace />} />,
    <Route key="gct" path="green-card-timeline" element={<Navigate to="/gc/green-card-timeline" replace />} />,
    <Route key="gcl" path="green-card-lottery" element={<GreenCardLotteryPage />} />,
    <Route key="dvf" path="how-to-fill-dv-lottery-form" element={<DVFormGuidePage />} />,
    <Route key="dvd" path="dv-lottery-dates" element={<DVLotteryDatesPage />} />,
    <Route key="dvp" path="dv-lottery-photo-requirements" element={<DVPhotoRequirementsPage />} />,
    <Route key="dvw" path="what-to-do-after-winning-dv-lottery" element={<DVAfterWinningPage />} />,
    <Route key="dvm" path="dv-lottery-common-mistakes" element={<DVCommonMistakesPage />} />,
    <Route key="dvcr" path="how-to-check-dv-lottery-results" element={<DVCheckResultsPage />} />,
    <Route key="dve" path="dv-lottery-eligibility" element={<DVEligibilityPage />} />,
    <Route key="ar" path="actively-reviewed-uscis-meaning" element={<ActivelyReviewedPage />} />,
    <Route key="ca" path="case-approved-what-next" element={<CaseApprovedPage />} />,
    <Route key="privacy" path="privacy" element={<PrivacyPage />} />,
    <Route key="terms" path="terms" element={<TermsPage />} />,
    <Route key="contact" path="contact" element={<ContactPage />} />,
    <Route key="dmv" path="dmv-guide" element={<DMVGuidePage />} />,
    <Route key="dmv-state" path="dmv-guide/:stateCode" element={<DMVGuidePage />} />,
  ];
}

function gcRoutes(basePath) {
  return (
    <Route path={basePath} element={<GCLayout />}>
      <Route index element={<GCLanding />} />
      <Route path="green-card-guide" element={<GCGuidePage />} />
      <Route path="green-card-timeline" element={<GCTimelinePage />} />
      <Route path="documents-checklist" element={<GCDocumentsPage />} />
      <Route path="faq" element={<GCFAQPage />} />
    </Route>
  );
}

function AppRouter() {
  const location = useLocation();

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously - before ProtectedRoute runs
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // greencardguide.us → render only the GC site at root
  if (isGCDomain()) {
    return (
      <Routes>
        {gcRoutes("/")}
      </Routes>
    );
  }

  const isGCSite = location.pathname.startsWith("/gc");

  return (
    <>
      {!isGCSite && <Navbar />}
      <Routes>
        {/* GreenCardGuide mini-site at /gc on main domain */}
        {gcRoutes("/gc")}

        {/* Localized public routes */}
        {LANGS.map((lang) => (
          <Route key={lang} path={`/${lang}`} element={<LocaleLayout lang={lang} />}>
            {publicRoutes()}
          </Route>
        ))}

        {/* Default English public routes - wrapped with LocaleLayout to reset language */}
        <Route path="/" element={<LocaleLayout lang="en" />}>
          <Route index element={<LandingPage />} />
          <Route path="auth" element={<AuthPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="guides" element={<FormGuidePage />} />
          <Route path="guides/:formId" element={<FormGuidePage />} />
          <Route path="green-card" element={<Navigate to="/gc" replace />} />
          <Route path="green-card-timeline" element={<Navigate to="/gc/green-card-timeline" replace />} />
          <Route path="green-card-lottery" element={<GreenCardLotteryPage />} />
          <Route path="how-to-fill-dv-lottery-form" element={<DVFormGuidePage />} />
          <Route path="dv-lottery-dates" element={<DVLotteryDatesPage />} />
          <Route path="dv-lottery-photo-requirements" element={<DVPhotoRequirementsPage />} />
          <Route path="what-to-do-after-winning-dv-lottery" element={<DVAfterWinningPage />} />
          <Route path="dv-lottery-common-mistakes" element={<DVCommonMistakesPage />} />
          <Route path="how-to-check-dv-lottery-results" element={<DVCheckResultsPage />} />
          <Route path="dv-lottery-eligibility" element={<DVEligibilityPage />} />
          <Route path="actively-reviewed-uscis-meaning" element={<ActivelyReviewedPage />} />
          <Route path="case-approved-what-next" element={<CaseApprovedPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="dmv-guide" element={<DMVGuidePage />} />
          <Route path="dmv-guide/:stateCode" element={<DMVGuidePage />} />
        </Route>

        {/* Protected routes — wrapped in LocaleLayout so stored language is restored */}
        <Route element={<LocaleLayout lang="en" />}>
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/billing/success" element={<BillingSuccessPage />} />
          <Route path="/billing/cancel" element={<BillingCancelPage />} />
          <Route path="/forms" element={<ProtectedRoute><FormExplainer /></ProtectedRoute>} />
          <Route path="/forms/:formId" element={<ProtectedRoute><FormExplainer /></ProtectedRoute>} />
          <Route path="/wizard/:formId" element={<ProtectedRoute><WizardPage /></ProtectedRoute>} />
          <Route path="/roadmaps" element={<ProtectedRoute><RoadmapPage /></ProtectedRoute>} />
          <Route path="/roadmaps/:roadmapId" element={<ProtectedRoute><RoadmapPage /></ProtectedRoute>} />
          <Route path="/checklists" element={<ProtectedRoute><ChecklistPage /></ProtectedRoute>} />
          <Route path="/deadlines" element={<ProtectedRoute><DeadlineTracker /></ProtectedRoute>} />
          <Route path="/documents" element={<ProtectedRoute><DocumentsPage /></ProtectedRoute>} />
          <Route path="/timeline" element={<ProtectedRoute><TimelinePage /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
